import { message } from 'antd'
import { TFunction } from 'i18next'
import * as todoManager from '../modules/TodoManager'
import * as todoService from '../modules/TodoService'
import { TodoItem } from '../modules/TodoItem'

export interface DeleteResult {
  deletedCount: number
  failedCount: number
}

/**
 * Todo delete - upload to server
 *
 * @param {TodoItem} todo - 삭제할 TodoItem
 * @param {TFunction} t - i18n translation 함수
 * @returns {Promise<void>}
 */
export const deleteTodoItem = (todo: TodoItem, t: TFunction): Promise<void> => {
  return new Promise((resolve, reject) => {
    todoManager.deleteTask(todo, (success: boolean) => {
      if (success) {
        message.success(t('todo.editor.taskDeleted'))
        resolve()
      } else {
        message.error(t('todo.editor.deleteFailed'))
        reject(new Error('Delete failed'))
      }
    })
  })
}

/**
 * 여러 할일들을 한번에 삭제합니다 (UI 업데이트 중복 방지)
 *
 * @param {TodoItem[]} todos - 삭제할 할일 목록
 * @returns {Promise<DeleteResult>}
 */
export const deleteTodoItems = async (todos: TodoItem[]): Promise<DeleteResult> => {
  if (!todos || todos.length === 0) {
    return { deletedCount: 0, failedCount: 0 }
  }

  let deletedCount = 0
  let failedCount = 0

  // 1. 먼저 모든 아이템을 캐시에서 즉시 제거 (smooth UI)
  try {
    for (const todo of todos) {
      await todoService.deleteTodo(todo.url, todo.folderUrl)
    }
  } catch (error) {
    console.error('[deleteTodoItems] Failed to remove todos from cache:', error)
  }

  // 2. UI 즉시 업데이트 (한번만)
  try {
    const { todoItemEmitter } = await import('../modules/TodoSync')
    todoItemEmitter.emit('item')
  } catch (error) {
    console.error('[deleteTodoItems] Failed to update UI:', error)
  }

  // 3. 백그라운드에서 서버 삭제 처리
  const { getClient } = await import('pages/calendar/modules/CalendarClient')
  const client = getClient()

  for (const todo of todos) {
    try {
      await client.deleteCalendarObject({
        calendarObject: {
          url: todo.url,
          etag: todo.etag,
        },
      })
      deletedCount++
    } catch (error) {
      failedCount++
      console.error(`[deleteTodoItems] Server delete failed: ${todo.summary}`, error)

      // 서버 삭제 실패 시 캐시에 복원
      try {
        await todoService.saveTodo(todo)
      } catch (restoreError) {
        console.error(`[deleteTodoItems] Failed to restore to cache: ${todo.summary}`, restoreError)
      }
    }
  }

  // 4. 실패가 있었거나 성공한 경우 최종 UI 업데이트
  if (deletedCount > 0 || failedCount > 0) {
    try {
      const { todoItemEmitter } = await import('../modules/TodoSync')
      todoItemEmitter.emit('item')
    } catch (error) {
      console.error('[deleteTodoItems] Failed final UI update:', error)
    }
  }

  return { deletedCount, failedCount }
}

export const deleteRecurrenceTodoItem = (
  todo: TodoItem,
  allTasks: boolean,
  t: TFunction,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    todoManager.deleteRecurringTask(todo, allTasks, (success: boolean) => {
      if (success) {
        message.success(t('todo.editor.taskDeleted'))
        resolve()
      } else {
        message.error(t('todo.editor.deleteFailed'))
        reject(new Error('Delete failed'))
      }
    })
  })
}

/**
 * 완료된 할일들을 시간대별로 필터링하여 삭제합니다.
 *
 * @param {string} key - 삭제 범위 키 ('year', 'month6', 'month3', 'month1', 'all')
 * @param {TFunction} t - i18n translation 함수
 * @param {Function} [onSuccess] - 삭제 성공 시 콜백 함수
 * @param {Function} [onError] - 삭제 실패 시 콜백 함수
 * @returns {Promise<void>}
 */
export const deleteCompletedTodos = async (
  key: string,
  t: TFunction,
  onSuccess?: () => void,
  onError?: (error: Error) => void,
): Promise<void> => {
  try {
    // 1. 모든 완료된 할일 조회
    const allTodos = await todoService.getSyncTodoItems()
    const completedTodos = allTodos.filter((todo) => todo.status === 'COMPLETED')

    if (completedTodos.length === 0) {
      if (onSuccess) onSuccess()
      return
    }

    // 2. 시간대별 필터링
    const todosToDelete = getCompletedTodosToDelete(completedTodos, key)

    if (todosToDelete.length === 0) {
      message.info(t('todo.todolist.noTasksInRange'))
      if (onSuccess) onSuccess()
      return
    }

    // 3. 배치 삭제 처리 (UI 업데이트 중복 방지)
    const { deletedCount, failedCount } = await deleteTodoItems(todosToDelete)

    if (deletedCount > 0) {
      if (onSuccess) onSuccess()
    } else if (failedCount > 0) {
      if (onError) onError(new Error(`Failed to delete ${failedCount} todos`))
    }
  } catch (error) {
    console.error('[deleteCompletedTodos] Error during deletion:', error)
    if (onError) onError(error as Error)
  }
}

/**
 * 완료된 할일들을 시간대별로 필터링하여 삭제 대상 목록을 반환합니다.
 */
const getCompletedTodosToDelete = (completedTodos: TodoItem[], key: string): TodoItem[] => {
  if (key === 'all') {
    return completedTodos
  }

  const now = new Date()
  const cutoffDate = getCutoffDate(now, key)

  return completedTodos.filter((todo) => {
    if (!todo.completedDate) {
      return false
    }

    const completedDate = new Date(todo.completedDate)
    return completedDate < cutoffDate
  })
}

/**
 * 시간대 키에 따른 기준 날짜를 계산합니다.
 */
const getCutoffDate = (now: Date, key: string): Date => {
  const cutoffDate = new Date(now)

  switch (key) {
    case 'year':
      cutoffDate.setFullYear(now.getFullYear() - 1)
      break
    case 'month6':
      cutoffDate.setMonth(now.getMonth() - 6)
      break
    case 'month3':
      cutoffDate.setMonth(now.getMonth() - 3)
      break
    case 'month1':
      cutoffDate.setMonth(now.getMonth() - 1)
      break
    default:
      return now
  }

  return cutoffDate
}
